import { useEffect } from 'react';
import { isValidElement, type ReactElement } from 'react';
import { useLocation } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { mainNavGroups, settingsNavGroup, type NavItem } from '../components/layout/navigationData';

/**
 * usePageMeta — keeps the browser tab in sync with the active route.
 *
 * For every page listed in the navigation it sets:
 *   - the document title  ("<Page> · Dolder Reporting")
 *   - a per-page favicon: the page's Lucide nav icon rendered in white on a
 *     Dolder-green rounded square (data-URI SVG, no static files per page).
 *
 * Routes that are not in the navigation (redirects, unknown paths) fall back to
 * the default Dolder favicon (`/favicon.png`) and the default title.
 *
 * Mount once, high in the tree (see Layout).
 */

/** Dolder brand green, sampled from the corporate favicon. */
const BRAND_GREEN = '#42A342';
const DEFAULT_TITLE = 'Dolder Reporting';
const DEFAULT_ICON_HREF = '/favicon.png';

/** All navigable pages, longest path first so `/a/b` wins over `/a`. */
const NAV_ITEMS: NavItem[] = [
    ...mainNavGroups.flatMap((g) => g.items),
    ...settingsNavGroup.items,
].sort((a, b) => b.path.length - a.path.length);

function matchNavItem(pathname: string): NavItem | undefined {
    return NAV_ITEMS.find(
        (item) => pathname === item.path || pathname.startsWith(item.path + '/'),
    );
}

/**
 * Canonical page path for a pathname — collapses param routes onto their nav
 * entry (`/visit-report/:draftId` -> `/visit-report`). Used by the usage
 * telemetry (services/usage.ts) so events key on the page, not the URL.
 */
export function matchNavPath(pathname: string): string {
    return matchNavItem(pathname)?.path ?? pathname;
}

const toKebab = (s: string) => s.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
const escapeAttr = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Serialize a Lucide icon's inner SVG nodes (path/circle/…) to a markup string.
 *
 * lucide-react 0.294 builds each icon with `forwardRef`; the geometry lives in
 * the render function's children. We invoke that render function directly (no
 * react-dom/server dependency) and serialize the resulting elements. Returns ''
 * if the internal shape ever changes, so the caller falls back to the default.
 */
function lucideInnerSvg(Icon: LucideIcon): string {
    try {
        const render = (Icon as unknown as {
            render?: (props: object, ref: unknown) => ReactElement;
        }).render;
        if (typeof render !== 'function') return '';

        const svg = render({}, null);
        const children = (svg?.props as { children?: unknown } | undefined)?.children;
        const nodes = Array.isArray(children) ? children.flat(Infinity) : [children];

        return nodes
            .filter(
                (c): c is ReactElement =>
                    isValidElement(c) && typeof (c as ReactElement).type === 'string',
            )
            .map((el) => {
                const tag = el.type as string;
                const props = el.props as Record<string, unknown>;
                const attrs = Object.keys(props)
                    .filter((k) => k !== 'children' && k !== 'key' && props[k] != null)
                    .map((k) => `${toKebab(k)}="${escapeAttr(String(props[k]))}"`)
                    .join(' ');
                return `<${tag}${attrs ? ' ' + attrs : ''}/>`;
            })
            .join('');
    } catch {
        return '';
    }
}

/** Build a data-URI favicon: white Lucide icon on a Dolder-green rounded tile. */
function buildFaviconDataUri(innerSvg: string): string {
    const svg =
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">` +
        `<rect width="32" height="32" rx="7" fill="${BRAND_GREEN}"/>` +
        `<svg x="6" y="6" width="20" height="20" viewBox="0 0 24 24" fill="none" ` +
        `stroke="#ffffff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">` +
        innerSvg +
        `</svg></svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

function setFavicon(href: string, type: string): void {
    let link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
    }
    link.type = type;
    link.href = href;
}

export function usePageMeta(): void {
    const { pathname } = useLocation();

    useEffect(() => {
        const item = matchNavItem(pathname);

        document.title = item ? `${item.label} · ${DEFAULT_TITLE}` : DEFAULT_TITLE;

        const innerSvg = item ? lucideInnerSvg(item.icon) : '';
        if (innerSvg) {
            setFavicon(buildFaviconDataUri(innerSvg), 'image/svg+xml');
        } else {
            setFavicon(DEFAULT_ICON_HREF, 'image/png');
        }
    }, [pathname]);
}
